import { CSpinner, useColorModes } from '@coreui/react-pro';
import { JSX, lazy, StrictMode, Suspense, useEffect } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import ReactGA from "react-ga4";
import { useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from "react-router";

import './scss/style.scss';

import NavigationProgress from './components/navigation-progress/NavigationProgress';
import { colorModes, featuresFlags, isExtCharts } from './helpers/helpers';
import { ProtectedRoute, PublicRoute } from './RouteGuards';
import routes from './routes';
import { JWTTokenManager } from 'statotest-api-requests/dist/helper';

// Containers
const DefaultLayout = lazy(() => import('./layout/DefaultLayout'))

// Pages
const Login = lazy(() => import('./views/pages/login/Login'))
const Register = lazy(() => import('./views/pages/register/Register'))
const Page404 = lazy(() => import('./views/pages/page404/Page404'))
const Page500 = lazy(() => import('./views/pages/page500/Page500'))

const fallbackRender = ({ error, resetErrorBoundary }: FallbackProps): JSX.Element => {
  // Type assertion needed as FallbackProps.error is typed as 'any' in react-error-boundary
  const typedError = error as Error;
  return <Page500 error={typedError} resetErrorBoundary={resetErrorBoundary} />;
};

const App = (): JSX.Element => {
  const { isColorModeSet, setColorMode } = useColorModes(colorModes.theme)
  interface RootState { theme?: string }
  const storedTheme = useSelector((state: RootState) => state.theme)

  useEffect(() => {
    ReactGA.initialize("G-3RFX78YPS1");
    const urlParams = new URLSearchParams(window.location.href.split('?')[1])
    const theme = urlParams.get('theme');
    // console.info("App theme: ", theme);
    if (theme && (/^[A-Za-z0-9\s]+/.exec(theme))) { setColorMode(theme) }

    if (isColorModeSet()) { return }
    if (storedTheme) { setColorMode(storedTheme) }
    // else { setColorMode('light') } // Set default theme to 'light' if no theme is stored
  }, [isColorModeSet, setColorMode, storedTheme])

  useEffect(() => {
    // Normalize: MessageEvent.origin nikdy nemá trailing slash, env může omylem mít.
    let modelsOrigin = 'https://models.statotest.com';
    try {
      modelsOrigin = new URL(import.meta.env.VITE_MODELS_BASE_URL ?? modelsOrigin).origin;
    } catch {}

    const handleMessage = async (e: MessageEvent) => {
      if (e.origin !== modelsOrigin) return;

      const raw: unknown = e.data;
      if (typeof raw !== 'object' || raw === null) return;
      const msg = raw as { type?: unknown };
      if (msg.type !== 'senzoracek:refresh-token') return;

      try {
        await JWTTokenManager.getInstance().forceRetrieveNewToken();
        const token = localStorage.getItem('token');
        if (token && e.source) {
          // Cast: e.source je MessageEventSource union; iframe parent je vždy Window,
          // a Window.postMessage legacy 3-arg signatura je kompatibilnější napříč
          // Brave/strict-mode kontexty než options-object forma.
          (e.source as Window).postMessage({ type: 'senzoracek:new-token', token }, e.origin);
        }
      } catch (error) {
        console.error('Failed to refresh token for Models:', error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);


  const appContent = (
    <ErrorBoundary fallbackRender={fallbackRender}>
      <BrowserRouter>
        <NavigationProgress />
        <Suspense
          fallback={
            <div className='d-flex vh-100 justify-content-center align-items-center'>
              <CSpinner color='primary' />
            </div>
          }
        >
          <Routes>
            {/* Public routes - redirect to dashboard if logged in */}
            {(featuresFlags.routes.login) && (
              <Route path="login" element={<PublicRoute><Login /></PublicRoute>} />
            )}
            {(featuresFlags.routes.register) && (
              <Route path="register" element={<PublicRoute><Register /></PublicRoute>} />
            )}

            {/* Error pages - always accessible */}
            {(featuresFlags.routes["404"]) && (
              <Route path="404" element={<Page404 />} />
            )}

            {/* All protected routes go through DefaultLayout */}
            <Route element={<ProtectedRoute><DefaultLayout /></ProtectedRoute>}>
              {/* Root redirect */}
              {!isExtCharts && (
                <Route index element={<Navigate to="/dashboard" replace />} />
              )}

              {/* Map all app routes dynamically */}
              {routes.map((route, idx) => route.element && (
                <Route
                  key={idx}
                  path={route.path}
                  element={<route.element />}
                />
              ))}

              {/* Catch-all for unknown routes - redirect to 404 */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  )
  if (import.meta.env.DEV) {
    return (<StrictMode>{appContent}</StrictMode>)
  }
  return appContent;
}

export default App
